import { PerformaSessionState } from "app/appModels";
import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { AccountAccessType, AppSettingsResponse, UserType } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import i18next from "i18next";
import { jwtDecode } from "jwt-decode";
import LibUtils from "lib/LibUtils";
import React, { useEffect, useRef, useState } from "react";
import { AnonymousUser, AuthJwt, AuthJwtDecoded, AuthRefreshUserProps, AuthService, AuthUser } from "services/authService";
import { reloadloadI18nConfig } from "services/i18n";
import { SessionService } from "services/sessionService";
import { SessionStore } from "services/SessionStore";

///////////////////////////////////////////////////////////////////////////////

type SessionContextType = {
    config: AppSettingsResponse;
    appBuild: AppBuild | null;

    isAuthenticated: boolean;
    user: AuthUser;
    refreshUserProperties: (newProps: AuthRefreshUserProps) => void;

    createSession: (p: AuthJwt) => void;
    finalizeSession: (callback?: VoidFunction) => void;
    clearSession: (callback?: VoidFunction) => void;

    uiCulture: string;
    canCallIaRequests: boolean;
    getLocaleName(locale: string): string;
    changeUiCulture: (culture: string) => void;
    changeTheme: (theme: string) => void;

    permissionExplorer: PermissionExplorer;
};

export const SessionContext = React.createContext<SessionContextType>(undefined!);

export const useSession = () => React.useContext(SessionContext);

/////////////////////////////////////////////////////////////////////////

interface Props {
    initialLocale: string;
    children?: React.ReactNode;
    splashScreen: React.FC<{ mode: PerformaSessionState; lastError: any; }>;
}

/**
 * Fornece o Contexto de Sessão de Usuário.
 * @param param0
 */
export const SessionProvider: React.FC<Props> = (props) => {
    const { children, initialLocale, splashScreen: SplashScreen } = props;
    const [settings, setSettings] = useState<AppSettingsResponse | null>(null);
    const [mode, setMode] = useState<PerformaSessionState>("init");
    const [lastError, setLastError] = useState<any>(null);
    const [uiCulture, setUiCulture] = useState<string>(initialLocale);
    const [contextBuilt, setContextBuilt] = useState<boolean>(false);
    const [user, setUser] = useState<AuthUser>(SessionStore.readUser());
    const [contextValue, setContextValue] = useState<SessionContextType>(undefined!);
    const [appBuild, setAppBuild] = useState<AppBuild | null>(null);
    const lastLogout = useRef<Date | null>(null);

    /**
     * Atualiza as propriedades do usuário e INVOCA nova construção de contexto.
     * @param newUser
     */
    const internalUserUpdate = (newUser: AuthUser) => {
        setUiCulture(newUser.locale);
        setUser(newUser);
        setContextBuilt(false);
        SessionStore.storeUser(newUser);
    };

    /**
     * Atualiza o Idioma da Interface e INVOCA nova construção de contexto.
     * @param culture
     * @returns
     */
    const changeI18nCulture = (culture: string) => {
        return new Promise<void>((resolve) => {
            if (culture != i18next.language) {
                i18next.changeLanguage(culture, () => {
                    window.uiLanguage = culture;
                    resolve();
                });
            }
            else
                resolve();
        });
    };

    /**
     * Cria a sessão do usuário.
     * @param jwt
     */
    const createSession = (jwt: AuthJwt) => {
        const jd = jwtDecode(jwt.access_token) as AuthJwtDecoded;
        const typedAccessType = jd.access_type as keyof typeof AccountAccessType;
        const userType = jd.userType as keyof typeof UserType;
        const permissions = jwt.permissions ? jwt.permissions.split(',') : null;

        const user: AuthUser = {
            accessType: AccountAccessType[typedAccessType],
            userType: UserType[userType],
            userId: jd.kid,
            name: jd.name,
            email: jd.email,
            locale: jd.locale,
            keepConnected: jd.keep_connected === "true",
            signedAggrement: jd.signed_aggrement === "true",
            spyUserId: jd.spyUserId,
            spyUsername: jd.spyUsername,
            thumbnailUrl: jwt.thumbnailUrl,
            permissions: permissions!,
            continueAuthStamp: jwt.continueAuthStamp ?? undefined,
            lastUpdate: 0,
        };

        SessionService.current.clearCarouselData();
        SessionStore.storeJWT(jwt, false);
        SessionStore.storeUser(user);
        setUser(user);
        setUiCulture(user.locale);
        setContextBuilt(false);
    };

    /**
     * Logout: finaliza a sessão do usuário.
     * @param callback
     */
    const finalizeSession = (callback?: VoidFunction) => {
        SessionService.current.clearCarouselData();

        AuthService
            .accountLogout()
            .catch((e) => console.error(e))
            .finally(() => clearSession(callback));
    };

    /**
     * Abort: limpa os dados internos da sessão do usuário.
     * @param callback
     */
    const clearSession = (callback?: VoidFunction) => {
        AuthService.logout()
            .catch((e) => console.error(e))
            .finally(() => {
                lastLogout.current = new Date();
                SessionStore.clearJWT();
                SessionStore.storeUser(AnonymousUser);
                SessionStore.clearCountryCode();
                setUser(AnonymousUser);
                setUiCulture(initialLocale);
                if (callback) callback();
            });
    };

    /**
     * Atualiza propriedades do usuário.
     * @param u
     */
    const refreshUserProperties = (u: AuthRefreshUserProps) => {
        const newUser: AuthUser = { ...user, ...u };
        internalUserUpdate(newUser);
    };

    /**
     * Troca o idioma da interface.
     * @param culture
     */
    const changeUiCulture = (culture: string) => {
        changeI18nCulture(culture)
            .then(() => {
                window.uiLanguage = culture;
                setUiCulture(culture);
                setContextBuilt(false);
            });
    };

    /**
     * Troca o tema da interface.
     * @param themeName
     */
    const changeTheme = (themeName: string) => {
        setSettings(state => ({ ...state!, tenantUIConfig: { ...state!.tenantUIConfig, themeName } }));
        setContextBuilt(false);
    };

    /**
     * Obtém o nome do idioma preferido do usuário.
     * @param locale
     * @returns
     */
    const getLocaleName = (locale: string): string => {
        var localeDef = contextValue.config.inputLocales.find(x => x.locale == locale);
        if (localeDef) return localeDef.nameR.find(x => x.locale == contextValue.uiCulture)?.text ?? locale;
        return locale;
    };

    /**
     * Carrega a Configuração do Sistema.
     */
    async function loadSettings() {
        try {
            if (user.userId != "") {
                await changeI18nCulture(user.locale);
                setUiCulture(user.locale);
            }

            const previousSettings = LibUtils.deepCopy(settings);

            const currentSettings = await SessionService.getSettings();
            window.document.title = currentSettings.tenantUIConfig.title;
            window.timezones = currentSettings.timezones;

            if (!LibUtils.deepCompare(previousSettings, currentSettings)) {
                setSettings(currentSettings);
                setContextBuilt(false);
                reloadloadI18nConfig(SessionService.current.apiPaths.coreApi);
            }
        }
        catch (error: any) {
            setMode("error");
            setLastError(error);
        }
    }

    /**
     * Ao carregar pela primeira vez ou quando uma nova sessão for criada.
     */
    useEffect(() => {
        // Carrega a versão do APP.
        async function loadBuildNumber() {
            try {
                const response = await fetch("/build-number.json");
                if (response.status == 200) {
                    const json = await response.json();
                    return (json as AppBuild);
                }
            }
            catch {
                console.error("Cannot get PowerMinds's build info");
            }
            return null;
        }

        async function init() {
            var build = await loadBuildNumber();
            if (build != null && SessionService.current.checkNewSystemVersion(build.buildVersion)) {
                setMode("newRelease");
                setTimeout(() => {
                    window.location.reload();
                    //Mudar para o código abaixo se não funcionar.
                    //window.location.href = window.location.href,
                }, 2000);
            }
            else {
                setAppBuild(build);
                loadSettings();
            }
        }

        init();
    }, [lastLogout.current]);

    /**
     * Ao solicitar Novo Contexto.
     */
    useDidUpdateEffect(() => {
        if (!contextBuilt && settings) {

            window.uiLanguage = uiCulture;

            setContextValue({
                config: settings!,
                appBuild: appBuild,

                user,
                isAuthenticated: user.userId !== "",
                refreshUserProperties,

                createSession,
                finalizeSession,
                clearSession,

                canCallIaRequests: user.permissions.includes(CorePermissions.UserCallIaRequests),
                uiCulture,
                getLocaleName,
                changeUiCulture,
                changeTheme,



                permissionExplorer: new PermissionExplorer(user.permissions, user.userType)
            });

            changeI18nCulture(uiCulture).then(() => {
                setMode("ready");
                setContextBuilt(true);
            });
        }
    }, [contextBuilt, settings]);

    /**
     * Quando as Permissões do usuário forem alteradas.
     */
    useEffect(() => {
        function handleNewJwt(data: CustomEventInit<string>) {
            if (data.detail === "changed") {
                const token = SessionStore.readToken();
                if (token != null) {
                    const permissions = token.permissions.split(',') ?? [];

                    const areEqual = LibUtils.deepCompare(permissions, user.permissions);
                    if (!areEqual) {
                        const newUser = { ...user, permissions };
                        internalUserUpdate(newUser);
                    }
                }
            }
        }

        document.addEventListener("storeJWT", handleNewJwt);
        return () => {
            document.removeEventListener("storeJWT", handleNewJwt);
        };
    }, [user, contextBuilt]);

    ///////////////////////////////////////////////////////////////

    return mode != "ready"
        ? <SplashScreen mode={mode} lastError={lastError} />
        : (
            <SessionContext.Provider value={contextValue}>
                {
                    contextBuilt && children
                }
            </SessionContext.Provider>
        );
};
